import classNames from 'classnames';
import React from 'react';

import moment from 'moment';

import type { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import type { ZonedDateTime } from '@internationalized/date';

import { useDatePicker } from '@react-aria/datepicker';
import { useFocusRing } from '@react-aria/focus';
import { useLocale } from '@react-aria/i18n';
import { useHover } from '@react-aria/interactions';
import { ModalProvider } from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';

import { useDatePickerState } from '@react-stately/datepicker';

import type { AriaDatePickerProps, DateValue, TimeValue } from '@react-types/datepicker';

import { toTZDate } from '../field-date-picker-react-aria/utils';
import { Calendar } from './controls/calendar';
import { NormalButton } from './controls/date-picker-button/normal-button';
import { DatePickerContainer } from './controls/date-picker-container';
import { Dialog } from './controls/date-picker-dialog/dialog';
import { DialogTrigger } from './controls/date-picker-dialog/dialog-trigger';
import { DatePickerField } from './controls/date-picker-field';
import { DatePickerInput } from './controls/date-picker-input';
import { TimeField } from './controls/time-field';
import type { IProps as IBaseTimeProps } from './controls/time-field';

export const inDateValue = (value?: DateValue): TimeValue | undefined => {
  return React.useMemo(() => {
    return !!value && 'hour' in value ? value : undefined;
  }, [value]);
};

export interface ITimeFieldProps
  extends Omit<IBaseTimeProps, 'maxValue' | 'minValue' | 'placeholderValue'> {
  maxValue?: DateValue;
  minValue?: DateValue;
  placeholderValue?: DateValue;
}

export const LocalTimeField: React.FC<ITimeFieldProps> = ({
  maxValue: propMaxValue,
  minValue: propMinValue,
  placeholderValue: propPlaceholderValue,
  ...props
}) => {
  const placeholderValue = inDateValue(propPlaceholderValue);
  const minValue = inDateValue(propMinValue);
  const maxValue = inDateValue(propMaxValue);

  return (
    <TimeField
      {...props}
      label="Time"
      marginTop="size-100"
      maxValue={maxValue}
      minValue={minValue}
      placeholderValue={placeholderValue}
    />
  );
};

export const useCoersion = (
  value?: string | Date | null | undefined
): ZonedDateTime | undefined => {
  // hack to coerce ISOString and empty string into date/null
  return React.useMemo(() => {
    if (!value) return undefined;

    return toTZDate(value);
  }, [value]);
};

export const granulairitySet = new Set(['hour', 'minute', 'second', 'millisecond']);

export interface IDatePickerAriaProps
  extends Omit<AriaDatePickerProps<DateValue>, 'value' | 'onChange'> {
  className?: string;
  errorText?: string;
  hideCalendarSelection?: boolean;
  isQuiet?: boolean;
  maxVisibleMonths?: number;
  submitted?: boolean;
  value?: string | Date | null;

  onChange: (value: string) => void;
}

const DatePickerAria: React.FC<IDatePickerAriaProps> = (props) => {
  const {
    autoFocus,
    errorText,
    granularity = 'day',
    isQuiet,
    isDisabled,
    isReadOnly,
    isRequired,
    maxVisibleMonths = 1,
    placeholderValue,
    submitted,
    hideCalendarSelection = false,

    onChange: propsOnChange
  } = props;

  const propValue = useCoersion(props.value);

  const error = React.useMemo<string | undefined>(() => {
    return !submitted || !errorText ? undefined : errorText.split(',').at(-1);
  }, [errorText, submitted]);

  const onChange = React.useCallback(
    (e: DateValue) => {
      const converted = moment((e as ZonedDateTime).toDate());
      propsOnChange?.(converted.toISOString(false));
    },
    [propsOnChange]
  );

  const { hoverProps, isHovered } = useHover({ isDisabled });
  const targetRef = React.useRef<HTMLDivElement>(null);
  const state = useDatePickerState({
    ...props,
    value: propValue,
    granularity,
    onChange
  });
  const { groupProps, labelProps, fieldProps, errorMessageProps, buttonProps, dialogProps } =
    useDatePicker(
      {
        ...props,
        onChange: onChange,
        value: propValue
      },
      state,
      targetRef
    );
  const { value, setValue, isOpen, setOpen } = state;
  const { direction } = useLocale();

  const { isFocused, isFocusVisible, focusProps } = useFocusRing({
    within: true,
    isTextInput: true,
    autoFocus
  });

  const className = React.useMemo(() => {
    return classNames(
      'click-input click-input-light mb-1 pl-2 w-full flex justify-between',
      props.className,
      {
        'bg-white': !isDisabled,
        'quiet-class': isQuiet,
        'invalid-class': state.validationState === 'invalid',
        'bg-gray-100': isDisabled,
        'is-disabled-class': isDisabled,
        'is-hovered-class': isHovered,
        'is-focused-class': isFocused,
        'focus-ring-class': isFocusVisible
      }
    );
  }, [isQuiet, state.validationState, isDisabled, isHovered, isFocused, isFocusVisible]);

  const fieldClassName = React.useMemo(() => {
    return classNames('input-class flex', {
      'is-disabled-class': isDisabled,
      'is-invalid-class': state.validationState === 'invalid'
    });
  }, [isDisabled, state.validationState]);

  const showTimeField = React.useMemo(() => {
    return granulairitySet.has(state.granularity);
  }, [state.granularity]);

  return (
    <div className={'field-container'}>
      <DatePickerContainer
        {...props}
        ref={targetRef}
        labelProps={labelProps}
        errorMessageProps={errorMessageProps}
        validationState={state.validationState}
        UNSAFE_className={'datepicker-style'}
      >
        <div {...mergeProps(groupProps, hoverProps, focusProps)} className={className}>
          <DatePickerInput
            isDisabled={isDisabled}
            isQuiet={isQuiet}
            validationState={state.validationState}
            autoFocus={autoFocus}
            className={'style'}
            inputClassName={fieldClassName}
          >
            <DatePickerField
              {...fieldProps}
              id={props.id}
              data-testid="date-field"
              validationState={state.validationState}
              value={value}
              onChange={setValue}
              placeholderValue={placeholderValue}
              isDisabled={isDisabled}
              isReadOnly={isReadOnly}
              isRequired={isRequired}
              granularity={granularity}
              hourCycle={props.hourCycle}
              inputClassName={fieldClassName}
              hideTimeZone={true}
            />
          </DatePickerInput>
          <DialogTrigger
            type="popover"
            mobileType="tray"
            placement={direction === 'rtl' ? 'bottom right' : 'bottom left'}
            targetRef={targetRef}
            hideArrow
            isOpen={isOpen}
            onOpenChange={setOpen}
          >
            {!hideCalendarSelection && (
              <NormalButton
                {...buttonProps}
                UNSAFE_className=""
                isQuiet={isQuiet}
                validationState={state.validationState}
                isDisabled={isDisabled || isReadOnly}
                data-testid={`icon-${props.id}`}
              >
                <FontAwesomeIcon icon={faCalendar as IconLookup} />
              </NormalButton>
            )}
            <Dialog UNSAFE_className="dialog-style border rounded-md" {...dialogProps}>
              <div className={'dialog-content-style bg-white'}>
                <Calendar
                  autoFocus
                  maxValue={props.maxValue}
                  minValue={props.minValue}
                  value={state.dateValue}
                  visibleMonths={maxVisibleMonths}
                  onChange={state.setDateValue}
                />
                {!!showTimeField && (
                  <div className={'showTime-field'}>
                    <LocalTimeField
                      granularity={state.granularity as IBaseTimeProps['granularity']}
                      hideTimeZone={props.hideTimeZone}
                      hourCycle={props.hourCycle}
                      label="Time"
                      marginTop="size-100"
                      maxValue={props.maxValue}
                      minValue={props.minValue}
                      placeholderValue={placeholderValue}
                      value={state.timeValue}
                      onChange={state.setTimeValue}
                    />
                  </div>
                )}
              </div>
            </Dialog>
          </DialogTrigger>
        </div>
      </DatePickerContainer>
      {!!error && (
        <div className="pb-2 text-xs font-medium text-rose-500">
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

class DatePickerFields extends React.Component<IDatePickerAriaProps> {
  render() {
    return (
      <ModalProvider>
        <DatePickerAria {...this.props} />
      </ModalProvider>
    );
  }
}

export default DatePickerFields;
